import * as React from "react";
import { Form, useFormApi } from "informed";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { Link, useParams } from "react-router-dom";
import { MultiSelect } from "../../../forms/informedv2/MultiSelect";
import { TextBox } from "../../../forms/informedv2/TextBox";
import { ActionButtons } from "../../../misc/ActionButtons";
import { AuditModal, AuditForm } from "../../../auditmodal/components/AuditModal";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { WebPortalClient, DiaryConfiguration } from "webportalclient";
import { IntegerValidator, MinMaxValidator } from "../../../forms/validation/NumericValidators";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import { ConvertToSelectListItemArray } from "./ParameterSelectList";
import { TextBoxWithStaticSuffix } from "../../../forms/informedv2/TextBoxWithStaticSuffix";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { useArrayField } from "informed";
import { useCookieRedirect } from "../../../misc/CookieRedirect";
import { TimePicker } from "../../../forms/informedv2/TimePicker";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { ISelectListItem } from "../../../misc/AspnetSelectList";
import * as moment from "moment";
import { Checkbox } from "../../../forms/informedv2/Checkbox";
import { nameof } from 'ts-simple-nameof';

interface DiarySettingsScreenFormState {
    diaryConfiguration: DiaryConfiguration;
    minAlarms: number;
    maxAlarms: number;
    pefBaselineCalculationMethod: { value: string; text: string };
    deviceParameters: ISelectListItem[];
    dailyPefDeclineAlert: number;
    defaultEdiaryAlarms: string[];
    displayQuestionnaireScoreSetting: boolean;
    diaryGradesEnabled: boolean;
    startVisitFromEveningWindow: boolean;
}

interface DiarySettingsScreenFormProps {
    studyId: string;
    diaryConfigurations: DiaryConfiguration[];
    calculationMethods: { value: string; text: string }[];
    deviceParameters: any[];
}

declare const rootUrl;

const displayQuestionnaireScoreSetting = "Vitalograph.WebPortal.Data.Settings.EDiary.DisplayQuestionnaireScoreSetting";
export const DiarySettingsScreenForm = ({ studyId, diaryConfigurations, calculationMethods, deviceParameters }: DiarySettingsScreenFormProps) => {
    const client = useWebPortalApiClient();
    const formApi = useFormApi<DiarySettingsScreenFormState>();
    const [alertShown, setAlertShown] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const { refreshSettings } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();
    const alarmFields = useArrayField({ field: "defaultEdiaryAlarms" });

    const AddDefaultDiaryAlarmFields = () => {

        const addField = () => {
            if (alarmFields.fields.length <= 9) {
                setErrorAlert(null);
                alarmFields.add()
            }
            else {
                return setErrorAlert("Maximum Default Alarm limit of 10 reached");
            }
        };

        return (
            <>
                <div className="row">
                    {alarmFields.fields.map(({ field, key, remove }, i) => (
                        <div className="form-group col-sm-6" key={key}>
                            <TimePicker
                                key={key}
                                id={`defaultEdiaryAlarm${i}`}
                                field={`${field}`}
                                label="Alarm"
                                required={true}
                                placeholderText="HH:mm (24 hour format)"
                                dateFormat="HH:mm"
                                suffix={<i className="text-danger fas fa-times" onClick={() => remove && remove()} style={{ cursor: 'pointer' }} />}
                            />
                        </div>

                    ))}</div>
                <div className="row">
                    <div className="col-md-12">
                        <button id="AddAlarm" className="btn btn-primary btn-outline" type="button" onClick={addField}>Add Default Alarm</button>
                    </div>
                </div>

            </>
        );
    };

    const onPreAuditSubmit = () => {
        setAlertShown(false);
        setErrorAlert(null);
        setSubmitting(true);
        formApi.validate();

        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setAuditModalVisible(false);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        try {
            if (state.values?.diaryConfiguration?.id) {
                await client.client.patchStudy({
                    diaryConfiguration: state.values.diaryConfiguration.id,
                }, studyId);
            }

            const results: boolean[] = [];
            results.push(await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.EDiary.DiaryMinMaxAlarmsSetting", { Min: state.values.minAlarms, Max: state.values.maxAlarms }));
            if (state.values.deviceParameters) {
                results.push(await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.EDiary.DeviceParametersSetting", state.values.deviceParameters.map(p => p.Id)));
            }
            results.push(await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.EDiary.DailyPefDeclineAlertThresholdSetting", state.values.dailyPefDeclineAlert));
            if (state.values.defaultEdiaryAlarms) {
                await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.EDiary.DefaultEDiaryAlarmSetting", state.values.defaultEdiaryAlarms.map((alarm: any) => alarm))
            }
            results.push(await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.EDiary.DisplayQuestionnaireScoreSetting", state.values.displayQuestionnaireScoreSetting));
            results.push(await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.EDiary.DiaryGradesSetting", state.values.diaryGradesEnabled));
            results.push(await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.EDiary.StartVisitFromEveningWindowSetting", state.values.startVisitFromEveningWindow));

            if (results.every(r => r === true)) {
                setAlertShown(true);
                refreshSettings();
            }
        } catch (ex) {
            setErrorAlert(ex.message);
        }

        setSubmitting(false);
    }

    const parametersSelectList: ISelectListItem[] = deviceParameters.map(p => {
        return {
            Id: p.Id,
            Value: p.Keyword,
            Text: p.DisplayName + (p.UnitName ? ` (${p.UnitName})` : ''),
            Group: null,
            Selected: false,
            Disabled: false
        };
    });

    return (
        <>
            <Alert show={alertShown} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/studies`}>Studies list</Link>?
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-12">
                    <h2 className="text-primary">Diary Settings</h2>
                    <div className="panel panel-default" data-automationid="DiarySettingsPanel">
                        <div className="panel-heading">
                            <h3 className="panel-title">Device Configuration</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-sm-6">
                                    <label>Diary Configuration</label>
                                    <MultiSelect
                                        id="diaryConfiguration"
                                        field="diaryConfiguration"
                                        options={diaryConfigurations}
                                        labelKey="name"
                                        valueKey="id"
                                    />
                                </div>                         
                            </div>
                            <div className="row">
                                <div className="form-group col-sm-6">
                                    <div className="form-group">
                                        <Checkbox
                                            id="displayQuestionnaireScore"
                                            label="Display Questionnaire Score"
                                            automationId="display-score"
                                            field={nameof<DiarySettingsScreenFormState>(f => f.displayQuestionnaireScoreSetting)}
                                        />
                                        <p className="help-block">Configures whether the score is shown or not for supported questionnaires</p>
                                    </div>
                                </div>
                                <div className="form-group col-sm-6">   
                                    <Checkbox id="diaryGradesEnabled" label="Quality Grading" field="diaryGradesEnabled" />
                                    <p className="help-block">Configures whether the ATS session quality grading is displayed for home spirometry or not</p>
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-sm-6">
                                    <Checkbox id="startVisitFromEveningWindow" label="Start Visit from the Evening Window" field={nameof<DiarySettingsScreenFormState>(f => f.startVisitFromEveningWindow)} />
                                    <p className="help-block">Start Visit in the Diary Configuration from the Evening Window</p>
                                </div>
                                </div>
                            <div className="row">
                                <div className="form-group col-sm-6">
                                    <label>Device Parameters</label>
                                    <p className="help-block">The parameters that should be enabled on the device.</p>
                                    <MultiSelect
                                        id="deviceParameters"
                                        field="deviceParameters"
                                        options={parametersSelectList}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                                <div className="form-group col-sm-6">
                                    <label>Daily PEF Decline Alert Threshold</label>
                                    <p className="help-block">The percentage drop in PEF between two sessions in a single day that will trigger the alert. Set to empty to turn off the alert.</p>
                                    <TextBoxWithStaticSuffix<number, DiarySettingsScreenFormState>
                                        id="dailyPefDeclineAlert"
                                        field="dailyPefDeclineAlert"
                                        type="number"
                                        min="0"
                                        max="99"
                                        staticpart="%"
                                        validate={(value) => value ? IntegerValidator(value) || MinMaxValidator(value, 0, 99) : undefined}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-sm-6">
                                    <label>Minimum Alarms</label>
                                    <TextBox<number, DiarySettingsScreenFormState>
                                        id="minAlarms"
                                        field="minAlarms"
                                        type="number"
                                        min="0"
                                        max="10"
                                        validate={(value) => IntegerValidator(value) || MinMaxValidator(value, 0, 10)}
                                    />
                                </div>
                                <div className="form-group col-sm-6">
                                    <label>Maximum Alarms</label>
                                    <TextBox<number, DiarySettingsScreenFormState>
                                        id="maxAlarms"
                                        field="maxAlarms"
                                        type="number"
                                        min="0"
                                        max="10"
                                        validate={(value) => IntegerValidator(value) || MinMaxValidator(value, 0, 10)}
                                    />
                                </div>
                            </div>
                            {AddDefaultDiaryAlarmFields()}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <ActionButtons submitting={submitting} onSaveClick={() => onPreAuditSubmit()} automationIdPrefix="ActionsHeader" returnToListRoute="/studies" />
                        </div>
                    </div>
                </div>
                {auditModalVisible ? (
                    <AuditModal
                        header="Approval"
                        isSubmitting={submitting}
                        onClose={() => setAuditModalVisible(false)}
                        onSubmit={(audit) => onSubmit(audit)}
                    />
                ) : null}
            </div>
        </>
    );
}

export const DiarySettings = () => {
    const { id } = useParams<{ id: string }>();
    const [initialValues, setInitialValues] = React.useState<DiarySettingsScreenFormState>();
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const [diaryConfigs, setDiaryConfigs] = React.useState([]);
    const [parameters, setParameters] = React.useState([]);
    const [pefBaselineCalculationMethods, setPefBaselineCalculationMethods] = React.useState([{
        value: "BestPefFirstSession",
        text: "Best PEF from first session"
    }]);

    useCookieRedirect(id);

    React.useEffect(() => {
        const getSettings = async () => {
            const study = await client.client.getStudy(id);

            const diaryConfigResult = await client.client.getDiaryConfigurations();
            setDiaryConfigs(diaryConfigResult.value);

            const eDiaryParametersResult = await legacyClient.getParametersBySessionType(SessionTypes.EDiary);
            setParameters(eDiaryParametersResult);

            const settingsQuery = await legacyClient.getSettingsHelper(displayQuestionnaireScoreSetting, id);
            const diarySettings = await legacyClient.getStudyDiarySettings(id);

            const alarmString = diarySettings.DefaultEdiaryAlarm;
            if (alarmString !== undefined && alarmString != null) {
                const alarmTimeStrings = alarmString.slice(1, -1).split(',');

                const alarmTimes = alarmTimeStrings.map((timeString: string) => {
                    const [hours, minutes] = timeString.split(':');
                    return moment.duration(`${hours}:${minutes}`).format("hh:mm:ss", { trim: false });
                });


                if (diarySettings) {
                    setInitialValues({
                        maxAlarms: diarySettings.MinMaxAlarms.Max,
                        minAlarms: diarySettings.MinMaxAlarms.Min,
                        diaryConfiguration: diaryConfigResult.value.find(x => x.id === study.diaryConfiguration),
                        pefBaselineCalculationMethod: pefBaselineCalculationMethods.find(x => x.value === diarySettings.PefBaselineCalculationMethod),
                        deviceParameters: ConvertToSelectListItemArray(diarySettings.DeviceParameters.map(p => p), eDiaryParametersResult),
                        dailyPefDeclineAlert: diarySettings.DailyPefDeclineAlertThreshold,
                        defaultEdiaryAlarms: alarmTimes.map(alarm => alarm),
                        displayQuestionnaireScoreSetting: settingsQuery.Data.length > 0 ? settingsQuery.Data[0].Value === "True" : false,
                        diaryGradesEnabled: diarySettings.DiaryGrades,
                        startVisitFromEveningWindow: diarySettings.StartVisitFromEveningWindowSetting
                    });
                }
            } else {
                if (diarySettings) {
                    setInitialValues({
                        maxAlarms: diarySettings.MinMaxAlarms.Max,
                        minAlarms: diarySettings.MinMaxAlarms.Min,
                        diaryConfiguration: diaryConfigResult.value.find(x => x.id === study.diaryConfiguration),
                        pefBaselineCalculationMethod: pefBaselineCalculationMethods.find(x => x.value === diarySettings.PefBaselineCalculationMethod),
                        deviceParameters: ConvertToSelectListItemArray(diarySettings.DeviceParameters.map(p => p), eDiaryParametersResult),
                        dailyPefDeclineAlert: diarySettings.DailyPefDeclineAlertThreshold,
                        defaultEdiaryAlarms: null,
                        displayQuestionnaireScoreSetting: settingsQuery.Data.length > 0 ? settingsQuery.Data[0].Value === "True" : false,
                        diaryGradesEnabled: diarySettings.DiaryGrades,
                        startVisitFromEveningWindow: diarySettings.StartVisitFromEveningWindowSetting
                    });
                }
            }
        }

        getSettings();
    }, [client.client, id]);

    if (!initialValues) {
        return <LoadingWithMessage message="Loading Diary Settings..." />;
    }

    return (
        <Form noValidate initialValues={initialValues}>
            <DiarySettingsScreenForm studyId={id} diaryConfigurations={diaryConfigs} calculationMethods={pefBaselineCalculationMethods} deviceParameters={parameters} />
        </Form>
    );
}
